import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileCheck2, Headphones, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { NIGERIA_STATES, getLgasForState, isValidLgaForState } from '../../lib/nigeriaLocations';
import { getApiErrorMessage } from '../../lib/apiError';

const today = () => new Date().toISOString().slice(0, 10);
const newUploadSessionId = () => (
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `cca-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const initialForm = {
  title: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  nationality: '',
  primaryPhone: '',
  secondaryPhone: '',
  whatsappNumber: '',
  email: '',
  street: '',
  neighbourhood: '',
  lga: '',
  state: '',
  proofOfAddressUpload: null,
  nin: '',
  bvn: '',
  validIdUpload: null,
  passportPhotoUpload: null,
  highestQualification: '',
  yearCompleted: '',
  certificateUpload: null,
  englishSpoken: '',
  englishWritten: '',
  otherLanguages: [],
  computerLiteracy: '',
  familiarWhatsAppBusiness: '',
  hasEmergencyCallExp: '',
  nokFullName: '',
  nokRelationship: '',
  nokPhone: '',
  consentAccuracy: false,
  consentBackgroundCheck: false,
  consentConfidentiality: false,
  consentMisconductDismissal: false,
  consentTraining: false,
  consentCodeOfConduct: false,
  declarationFullName: '',
  declarationSignature: '',
  declarationDate: today()
};

const sections = [
  {
    title: 'Personal Information',
    fields: [
      { key: 'title', label: 'Title', type: 'select', options: ['Mr', 'Mrs', 'Ms', 'Other'] },
      { key: 'firstName', label: 'First Name', required: true },
      { key: 'middleName', label: 'Middle Name' },
      { key: 'lastName', label: 'Last Name', required: true },
      { key: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true },
      { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'] },
      { key: 'nationality', label: 'Nationality', required: true }
    ]
  },
  {
    title: 'Contact Information',
    fields: [
      { key: 'primaryPhone', label: 'Primary Phone Number', type: 'tel', required: true },
      { key: 'secondaryPhone', label: 'Secondary Phone Number', type: 'tel' },
      { key: 'whatsappNumber', label: 'WhatsApp Number', type: 'tel' },
      { key: 'email', label: 'Primary Email Address', type: 'email', required: true }
    ]
  },
  {
    title: 'Residential Address',
    fields: [
      { key: 'street', label: 'House Number & Street', required: true },
      { key: 'neighbourhood', label: 'Area / Neighbourhood', required: true },
      { key: 'state', label: 'State', type: 'state', required: true },
      { key: 'lga', label: 'Local Government Area (LGA)', type: 'lga' },
      { key: 'proofOfAddressUpload', label: 'Upload: Proof of Address', type: 'file', required: true }
    ]
  },
  {
    title: 'Identity Verification',
    fields: [
      { key: 'nin', label: 'NIN', required: true, hint: 'Enter exactly 11 digits.' },
      { key: 'bvn', label: 'BVN', required: true, hint: 'Enter exactly 11 digits.' },
      { key: 'validIdUpload', label: 'Upload: Valid Government-Issued ID', type: 'file', required: true },
      { key: 'passportPhotoUpload', label: 'Upload: Passport Photograph', type: 'file', required: true }
    ]
  },
  {
    title: 'Education',
    fields: [
      { key: 'highestQualification', label: 'Highest Qualification', type: 'select', options: ['SSCE/WAEC', 'OND', 'HND', 'B.Sc/B.A', 'M.Sc/MBA', 'Other'], required: true },
      { key: 'yearCompleted', label: 'Year Completed', required: true },
      { key: 'certificateUpload', label: 'Upload: Certificate / Result Slip', type: 'file' }
    ]
  },
  {
    title: 'Communication & Technical Skills',
    fields: [
      { key: 'englishSpoken', label: 'English Proficiency (spoken)', type: 'select', options: ['Excellent', 'Very Good', 'Good', 'Fair'], required: true },
      { key: 'englishWritten', label: 'English Proficiency (written)', type: 'select', options: ['Excellent', 'Very Good', 'Good', 'Fair'], required: true },
      { key: 'otherLanguages', label: 'Other Languages Spoken', type: 'multi', options: ['Yoruba', 'Igbo', 'Hausa', 'Pidgin', 'French', 'Other'] },
      { key: 'computerLiteracy', label: 'Computer Literacy', type: 'select', options: ['Basic', 'Intermediate', 'Advanced'], required: true },
      { key: 'familiarWhatsAppBusiness', label: 'Familiar with WhatsApp Business?', type: 'select', options: ['Yes', 'No'], required: true },
      { key: 'hasEmergencyCallExp', label: 'Experience handling emergency/time-sensitive calls?', type: 'select', options: ['Yes', 'No'], required: true }
    ]
  },
  {
    title: 'Next of Kin',
    fields: [
      { key: 'nokFullName', label: 'Full Name', required: true },
      { key: 'nokRelationship', label: 'Relationship', type: 'select', options: ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'], required: true },
      { key: 'nokPhone', label: 'Phone Number', type: 'tel', required: true }
    ]
  },
  {
    title: 'Declaration',
    fields: [
      { key: 'consentAccuracy', label: 'Confirm all info true and accurate', type: 'checkbox', required: true },
      { key: 'consentBackgroundCheck', label: 'Consent to background checks', type: 'checkbox', required: true },
      { key: 'consentConfidentiality', label: 'Understand client info is strictly confidential', type: 'checkbox', required: true },
      { key: 'consentMisconductDismissal', label: 'Understand misconduct/breach = dismissal', type: 'checkbox', required: true },
      { key: 'consentTraining', label: 'Agree to complete required training before live calls', type: 'checkbox', required: true },
      { key: 'consentCodeOfConduct', label: 'Agree to Code of Conduct, Escalation Protocol, and policies', type: 'checkbox', required: true },
      { key: 'declarationFullName', label: 'Full Name (print)', required: true },
      { key: 'declarationSignature', label: 'Signature', required: true },
      { key: 'declarationDate', label: 'Date', type: 'date', required: true, disabled: true }
    ]
  }
];

const requiredFields = sections.flatMap(section => section.fields.filter(field => field.required));

const isFilled = (value, field) => {
  if (field.type === 'checkbox') return value === true;
  if (field.type === 'file') return Boolean(value?.fileUrl);
  if (field.type === 'multi') return Array.isArray(value) && value.length > 0;
  if (value === 0) return true;
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

const CreateCustomerCareAgentPage = () => {
  const navigate = useNavigate();
  const { authApi } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [uploadSessionId] = useState(newUploadSessionId);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [serverMissing, setServerMissing] = useState([]);
  const [createdAgentResult, setCreatedAgentResult] = useState(null);

  const missingFields = useMemo(() => {
    const missing = requiredFields.filter(field => !isFilled(form[field.key], field)).map(field => field.label);
    if (form.nin && !/^\d{11}$/.test(form.nin)) missing.push('NIN must be exactly 11 digits');
    if (form.bvn && !/^\d{11}$/.test(form.bvn)) missing.push('BVN must be exactly 11 digits');
    return missing;
  }, [form]);

  const updateField = (key, value) => {
    setForm(prev => {
      if (key === 'state') {
        return {
          ...prev,
          state: value,
          lga: isValidLgaForState(value, prev.lga) ? prev.lga : ''
        };
      }
      return { ...prev, [key]: value };
    });
    setServerMissing([]);
  };

  const toggleArrayValue = (key, option) => {
    setForm(prev => {
      const current = prev[key] || [];
      const next = current.includes(option) ? current.filter(item => item !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const uploadFile = async (field, file) => {
    if (!file) return;
    setError('');
    const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    const extensionAllowed = /\.(jpe?g|png|pdf)$/i.test(file.name);
    if (!allowed.includes(file.type) && !extensionAllowed) {
      setError('Upload .jpg, .jpeg, .png, or .pdf files only.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Each upload must be 5MB or smaller.');
      return;
    }
    const data = new FormData();
    data.append('fieldName', field.key);
    data.append('file', file);
    setUploading(prev => ({ ...prev, [field.key]: true }));
    setUploadProgress(prev => ({ ...prev, [field.key]: 0 }));
    try {
      const response = await authApi.post(`/admin/customer-care-agents/${uploadSessionId}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: event => {
          if (event.total) setUploadProgress(prev => ({ ...prev, [field.key]: Math.round((event.loaded * 100) / event.total) }));
        }
      });
      updateField(field.key, response.data);
    } catch (err) {
      setError(getApiErrorMessage(err, `Unable to upload ${field.label}.`));
    } finally {
      setUploading(prev => ({ ...prev, [field.key]: false }));
    }
  };

  const submitAgent = async (event) => {
    event.preventDefault();
    setError('');
    setServerMissing([]);
    setCreatedAgentResult(null);
    if (missingFields.length > 0) {
      setServerMissing(missingFields);
      setError('Complete all mandatory fields before creating this customer care agent.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await authApi.post('/admin/customer-care-agents', { ...form, uploadSessionId });
      setCreatedAgentResult(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.missingFields) {
        setServerMissing(detail.missingFields);
        setError(detail.message || 'Customer care agent onboarding is incomplete.');
      } else {
        setError(getApiErrorMessage(err, 'Unable to create customer care agent.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = form[field.key];
    const isMissing = serverMissing.includes(field.label);
    const commonClass = isMissing ? 'border-red-300 focus-visible:ring-red-400' : '';

    if (field.type === 'state') {
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={nextValue => updateField(field.key, nextValue)}>
            <SelectTrigger className={commonClass}><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>{NIGERIA_STATES.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'lga') {
      const lgas = getLgasForState(form.state);
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={nextValue => updateField(field.key, nextValue)} disabled={!form.state || lgas.length === 0}>
            <SelectTrigger className={commonClass}><SelectValue placeholder={form.state ? 'Select LGA' : 'Select state first'} /></SelectTrigger>
            <SelectContent>{lgas.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div className="space-y-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <Select value={value || ''} onValueChange={nextValue => updateField(field.key, nextValue)}>
            <SelectTrigger className={commonClass}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{field.options.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }

    if (field.type === 'multi') {
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <div className={`grid gap-2 rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4 sm:grid-cols-3`}>
            {field.options.map(option => (
              <label key={option} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                <Checkbox checked={(value || []).includes(option)} onCheckedChange={() => toggleArrayValue(field.key, option)} />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label key={field.key} className={`flex items-start gap-3 rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4 text-sm text-[#1A1A1A] sm:col-span-2`}>
          <Checkbox checked={Boolean(value)} onCheckedChange={checked => updateField(field.key, Boolean(checked))} />
          <span>{field.label}{field.required ? ' *' : ''}</span>
        </label>
      );
    }

    if (field.type === 'file') {
      const uploaded = Boolean(value?.fileUrl);
      return (
        <div className="space-y-2 sm:col-span-2" key={field.key}>
          <Label>{field.label}{field.required ? ' *' : ''}</Label>
          <div className={`rounded-lg border ${isMissing ? 'border-red-300' : 'border-[#E8E6E3]'} bg-[#FAFAF7] p-4`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${uploaded ? 'bg-green-100 text-green-700' : 'bg-[#F5E8EA] text-[#9F1D35]'}`}>
                  <FileCheck2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{uploaded ? value.fileName : 'No file uploaded yet'}</p>
                  <p className="text-xs text-[#5A5A5A]">JPG, PNG, or PDF. Max 5MB.</p>
                </div>
              </div>
              <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={event => uploadFile(field, event.target.files?.[0])} className="sm:max-w-[260px]" />
            </div>
            {uploading[field.key] && <div className="mt-3"><Progress value={uploadProgress[field.key] || 15} /></div>}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2" key={field.key}>
        <Label>{field.label}{field.required ? ' *' : ''}</Label>
        <Input type={field.type || 'text'} value={value ?? ''} disabled={field.disabled} onChange={event => updateField(field.key, event.target.value)} className={commonClass} />
        {field.hint && <p className="text-xs text-[#5A5A5A]">{field.hint}</p>}
      </div>
    );
  };

  const anyUploading = Object.values(uploading).some(Boolean);
  const canSubmit = missingFields.length === 0 && !anyUploading;

  return (
    <div className="min-h-screen bg-[#F7F4EE] px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link to="/admin/customer-care-agents" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#9F1D35]">
              <ArrowLeft className="h-4 w-4" />
              Back to agents
            </Link>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A]">Create customer care agent</h1>
            <p className="mt-1 text-sm text-[#5A5A5A]">Admin-only onboarding for support operations staff.</p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#F5E8EA] text-[#9F1D35]">
            <Headphones className="h-6 w-6" />
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <p>{error}</p>
            {serverMissing.length > 0 && <p className="mt-2 text-xs">{serverMissing.join(', ')}</p>}
          </div>
        )}

        {createdAgentResult && (
          <div className="mb-5 rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
            <p className="font-medium">Customer care agent created.</p>
            <p className="mt-1">{createdAgentResult.message || `Setup email sent to ${createdAgentResult.email}.`}</p>
            <p className="mt-1 text-xs text-green-700">They must use the setup link in that email to create their password before signing in.</p>
            <Button type="button" className="mt-3" onClick={() => navigate('/admin/customer-care-agents', { replace: true })}>View agents</Button>
          </div>
        )}

        <form onSubmit={submitAgent} className="space-y-5">
          {sections.map(section => (
            <section key={section.title} className="rounded-lg border border-[#E8E6E3] bg-white p-6">
              <h2 className="mb-5 font-serif text-2xl font-medium text-[#1A1A1A]">{section.title}</h2>
              <div className="grid gap-4 sm:grid-cols-2">{section.fields.map(renderField)}</div>
            </section>
          ))}

          <div className="sticky bottom-0 rounded-lg border border-[#E8E6E3] bg-white/95 p-4 backdrop-blur">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                {canSubmit ? <CheckCircle2 className="h-4 w-4 text-green-700" /> : <span className="h-2 w-2 rounded-full bg-amber-500" />}
                {canSubmit ? 'Ready to create' : `${missingFields.length} required item${missingFields.length === 1 ? '' : 's'} remaining`}
              </div>
              <Button type="submit" disabled={!canSubmit || submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Create Agent
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCustomerCareAgentPage;
